// /app/frontend/src/pages/risk/dpia/steps/ScopeStep.jsx
import React, { useState } from 'react';
import { Label } from '../../../../components/ui/label';
import { Textarea } from '../../../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Badge } from '../../../../components/ui/badge';
import { Button } from '../../../../components/ui/button';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Separator } from '../../../../components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../../components/ui/collapsible';
import {
  Plus, Settings, Trash2, Check, ChevronDown, ChevronUp, Database,
  Paperclip, FileText, Download,
} from 'lucide-react';
import { format } from 'date-fns';
import { INDIVIDUAL_CATEGORIES } from '../constants';
import { dpiaIntakeAPI } from '../../../../services/api';
import { toast } from 'sonner';

function humanFileSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0; let v = bytes;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

export default function ScopeStep({ formData, updateField }) {
  const [dataLifecycleOpen, setDataLifecycleOpen] = useState({
    collection: false,
    usage: false,
    storage: false,
    deletion: false
  });

  // Supporting documents attached by the project team via the public intake
  // form (spec §Supporting Document Upload). They're stored on the DPIA doc
  // at submit time, and surface here on the Scope tab per the client's ask.
  const supportingDocs = formData?.supporting_documents || [];
  const intakeId = formData?.intake_id;

  const handleDownloadSupportingDoc = async (doc) => {
    if (!intakeId) {
      toast.error('This DPIA was not created from an intake; the document reference is missing.');
      return;
    }
    try {
      const res = await dpiaIntakeAPI.downloadDocument(intakeId, doc.file_id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || 'document';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download document');
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Project Description</Label>
        <Textarea 
          value={formData.project_description}
          onChange={(e) => updateField('project_description', e.target.value)}
          placeholder="Describe the project and its objectives"
          rows={4}
          data-testid="project-description-textarea"
        />
      </div>

      {/* Categories of Individuals - Card-based selection */}
      <div className="space-y-3">
        <Label>Categories of Individuals</Label>
        <p className="text-sm text-muted-foreground">Select all categories of individuals whose data will be processed</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {INDIVIDUAL_CATEGORIES.map(cat => {
            const isSelected = formData.categories_of_individuals.includes(cat);
            return (
              <div 
                key={cat}
                onClick={() => {
                  const current = formData.categories_of_individuals;
                  const updated = current.includes(cat) 
                    ? current.filter(c => c !== cat)
                    : [...current, cat];
                  updateField('categories_of_individuals', updated);
                }}
                className={`
                  p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 text-center
                  ${isSelected 
                    ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/30' 
                    : 'border-border hover:border-teal-300 hover:bg-muted/50'}
                `}
                data-testid={`category-${cat.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center gap-2 justify-center">
                  <div className={`
                    w-4 h-4 rounded border flex items-center justify-center flex-shrink-0
                    ${isSelected ? 'border-teal-500 bg-teal-500' : 'border-muted-foreground/50'}
                  `}>
                    {isSelected && <Check className="h-3 w-3 text-white" />}
                  </div>
                  <span className={`text-sm font-medium ${isSelected ? 'text-teal-700 dark:text-teal-300' : ''}`}>
                    {cat}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        {formData.categories_of_individuals.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            <span className="text-xs text-muted-foreground mr-2">Selected:</span>
            {formData.categories_of_individuals.map(cat => (
              <Badge key={cat} variant="secondary" className="text-xs">
                {cat}
              </Badge>
            ))}
          </div>
        )}
      </div>

      <Separator />

      {/* Data Lifecycle - Expandable Sections */}
      <div className="space-y-3">
        <h4 className="font-medium">Data Lifecycle</h4>
        <p className="text-sm text-muted-foreground">Document how data is handled at each stage</p>
        
        {/* Collection */}
        <Collapsible 
          open={dataLifecycleOpen.collection} 
          onOpenChange={(open) => setDataLifecycleOpen(prev => ({...prev, collection: open}))}
        >
          <Card className="border border-border">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-950 flex items-center justify-center">
                      <Plus className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <CardTitle className="text-sm font-medium">How is data collected?</CardTitle>
                      <p className="text-xs text-muted-foreground">Methods and sources of data collection</p>
                    </div>
                  </div>
                  {dataLifecycleOpen.collection ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                <Textarea 
                  value={formData.data_collection_method}
                  onChange={(e) => updateField('data_collection_method', e.target.value)}
                  placeholder="Describe how data is collected (e.g., forms, APIs, manual entry, third-party sources)..."
                  rows={3}
                  data-testid="data-collection-textarea"
                />
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Usage */}
        <Collapsible 
          open={dataLifecycleOpen.usage} 
          onOpenChange={(open) => setDataLifecycleOpen(prev => ({...prev, usage: open}))}
        >
          <Card className="border border-border">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-950 flex items-center justify-center">
                      <Settings className="h-4 w-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <CardTitle className="text-sm font-medium">How is data used?</CardTitle>
                      <p className="text-xs text-muted-foreground">Processing activities and purposes</p>
                    </div>
                  </div>
                  {dataLifecycleOpen.usage ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                <Textarea 
                  value={formData.data_usage_method}
                  onChange={(e) => updateField('data_usage_method', e.target.value)}
                  placeholder="Describe how data is used (e.g., analytics, decision making, service delivery)..."
                  rows={3}
                  data-testid="data-usage-textarea"
                />
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Storage */}
        <Collapsible 
          open={dataLifecycleOpen.storage} 
          onOpenChange={(open) => setDataLifecycleOpen(prev => ({...prev, storage: open}))}
        >
          <Card className="border border-border">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-950 flex items-center justify-center">
                      <Database className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <CardTitle className="text-sm font-medium">How is data stored?</CardTitle>
                      <p className="text-xs text-muted-foreground">Storage locations and security measures</p>
                    </div>
                  </div>
                  {dataLifecycleOpen.storage ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                <Textarea 
                  value={formData.data_storage_method}
                  onChange={(e) => updateField('data_storage_method', e.target.value)}
                  placeholder="Describe how data is stored (e.g., cloud storage, on-premise servers, encryption)..."
                  rows={3}
                  data-testid="data-storage-textarea"
                />
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Deletion */}
        <Collapsible 
          open={dataLifecycleOpen.deletion} 
          onOpenChange={(open) => setDataLifecycleOpen(prev => ({...prev, deletion: open}))}
        >
          <Card className="border border-border">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-950 flex items-center justify-center">
                      <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                      <CardTitle className="text-sm font-medium">How is data deleted?</CardTitle>
                      <p className="text-xs text-muted-foreground">Retention periods and deletion procedures</p>
                    </div>
                  </div>
                  {dataLifecycleOpen.deletion ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                <Textarea 
                  value={formData.data_deletion_method}
                  onChange={(e) => updateField('data_deletion_method', e.target.value)}
                  placeholder="Describe how data is deleted (e.g., retention schedule, secure deletion methods, archiving)..."
                  rows={3}
                  data-testid="data-deletion-textarea"
                />
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      </div>

      <div className="flex items-center space-x-2 p-4 bg-muted/50 rounded-lg">
        <Checkbox
          id="generates_new"
          checked={formData.generates_new_data}
          onCheckedChange={(checked) => updateField('generates_new_data', checked)}
          className="data-[state=checked]:bg-teal-500 data-[state=checked]:border-teal-500"
          data-testid="generates-new-data-checkbox"
        />
        <Label htmlFor="generates_new" className="cursor-pointer">Does this project generate new personal data?</Label>
      </div>

      {/* Supporting documents from the intake form. Shown on the Scope tab
          per client request — the files relate to scope/context information
          about the project. */}
      {supportingDocs.length > 0 && (
        <div className="pt-4 border-t">
          <div className="flex items-center gap-2 mb-1">
            <Paperclip className="h-4 w-4 text-teal-600 dark:text-teal-400" />
            <Label className="text-sm font-medium">Supporting documents from intake</Label>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Attached by the submitter when they filled out the DPIA intake form.
          </p>
          <ul className="space-y-2" data-testid="dpia-supporting-docs-list">
            {supportingDocs.map(doc => (
              <li
                key={doc.file_id}
                className="flex items-center gap-3 px-3 py-2 rounded-md border bg-muted/20"
              >
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{doc.file_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {humanFileSize(doc.size)}
                    {doc.uploaded_at ? ` · uploaded ${format(new Date(doc.uploaded_at), 'PPP')}` : ''}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownloadSupportingDoc(doc)}
                  className="gap-1"
                  data-testid={`dpia-supporting-doc-download-${doc.file_id}`}
                >
                  <Download className="h-3.5 w-3.5" />
                  Download
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
